// ════════════════════════════════════════════════════════════════════
// thermalPrint.ts — Image-Based Print Agent Client (Server-Relay + LAN Fallback)
// Frontend renders receipt → Base64 PNG → Server dispatches to agent via WebSocket
// ════════════════════════════════════════════════════════════════════

export interface ReceiptData {
  storeName: string; storeAddress?: string; storePhone?: string;
  storeLogo?: string; date: string; time: string; refId: string; cashierName?: string;
  customer?: { id?: number; name: string; phone?: string; address?: string; points?: number } | null;
  items: Array<{ name: string; qty: number; price: number; total?: number; returnPolicy?: string; warrantyPeriod?: string }>;
  subtotal?: number; discountPct?: number; discountAmt?: number; grandTotal?: number;
  payments: Array<{ method: string; amount: number }>; isInvoice?: boolean;
}
export interface PrintResult { success: boolean; method: string; printer?: string; error?: string; }

const STORAGE_KEY = 'kangavena:printAgent';
interface AgentConfig { ip: string; secret: string; defaultPrinter: string; }

function readCache(): AgentConfig {
  try { const r = localStorage.getItem(STORAGE_KEY); if (r) { const p = JSON.parse(r); return { ip: p.ip||'', secret: p.secret||'', defaultPrinter: p.defaultPrinter||'' }; } } catch {}
  return { ip:'', secret:'', defaultPrinter:'' };
}
function writeCache(cfg: AgentConfig) { localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg)); }

// ── Cloud sync ──
export async function syncFromCloud(): Promise<AgentConfig> {
  try {
    const token = localStorage.getItem('kangavena:cashierToken') || localStorage.getItem('kangavena:posToken') || '';
    if (!token) return readCache();
    const res = await fetch('/api/store/print-agent', {
      headers: { 'Authorization': `Bearer ${token}` },
      signal: AbortSignal.timeout(5000)
    });
    if (res.ok) {
      const data = await res.json();
      if (data.success) {
        const cfg: AgentConfig = { ip: data.ip || '', secret: data.secret || '', defaultPrinter: data.defaultPrinter || '' };
        if (cfg.ip && cfg.secret) writeCache(cfg);
        return cfg;
      }
    }
  } catch {}
  return readCache();
}

export async function saveToCloud(ip: string, secret: string, defaultPrinter: string): Promise<boolean> {
  try {
    const token = localStorage.getItem('kangavena:cashierToken') || localStorage.getItem('kangavena:posToken') || '';
    if (!token) return false;
    const res = await fetch('/api/store/print-agent', {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ ip, secret, defaultPrinter }),
      signal: AbortSignal.timeout(5000)
    });
    if (res.ok) { writeCache({ ip, secret, defaultPrinter }); return true; }
  } catch {}
  return false;
}

export function getAgentConfig(): AgentConfig { return readCache(); }
export function setAgentConfig(ip: string, secret: string, defaultPrinter?: string): void {
  const cfg = { ip: ip.trim(), secret: secret.trim(), defaultPrinter: defaultPrinter || readCache().defaultPrinter || '' };
  writeCache(cfg);
  saveToCloud(cfg.ip, cfg.secret, cfg.defaultPrinter);
}
export function setDefaultPrinter(printer: string): void {
  const cfg = readCache(); cfg.defaultPrinter = printer; writeCache(cfg);
  saveToCloud(cfg.ip, cfg.secret, cfg.defaultPrinter);
}
export function clearAgentConfig(): void { localStorage.removeItem(STORAGE_KEY); }

export function getStoreAuthHeaders(explicitKey?: string): Record<string, string> {
  const cfg = readCache();
  const apiKey = explicitKey || cfg.secret || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('kangavena:storeApiKey') : '') || '';
  const cashierToken = typeof localStorage !== 'undefined' ? (localStorage.getItem('kangavena:cashierToken') || localStorage.getItem('kangavena:posToken') || '') : '';
  const headers: Record<string, string> = {};
  if (apiKey) headers['x-store-api-key'] = apiKey;
  if (cashierToken) headers['Authorization'] = `Bearer ${cashierToken}`;
  return headers;
}

export function isAgentConfigured(storeApiKey?: string): boolean {
  const c = readCache();
  if (c.ip && c.secret) return true;
  const token = storeApiKey || c.secret || (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('kangavena:storeApiKey') : '') || (typeof localStorage !== 'undefined' ? (localStorage.getItem('kangavena:cashierToken') || localStorage.getItem('kangavena:posToken')) : '') || '';
  return token !== '';
}

// ── Auto-pair: Try LAN first (same PC only), then mark as cloud-ready ──
export async function autoPair(storeToken: string, defaultPrinter?: string): Promise<{ success: boolean; printers?: string[]; error?: string }> {
  // Method 1: Try server API first (works from any device & zero firewall issues)
  try {
    const setupRes = await fetch('/api/printer/setup', {
      headers: getStoreAuthHeaders(storeToken),
      signal: AbortSignal.timeout(5000)
    });
    if (setupRes.ok) {
      const data = await setupRes.json();
      if (data.success && data.agentConnected) {
        writeCache({ ip: 'cloud', secret: storeToken, defaultPrinter: defaultPrinter || '' });
        return {
          success: true,
          printers: (data.printers || []).map((p: any) => p.name || p.connectionAddress).filter(Boolean)
        };
      }
    }
  } catch {}

  // Method 2: Try direct LAN connection (prioritizing 127.0.0.1)
  for (const host of ['127.0.0.1', 'localhost']) {
    try {
      const res = await fetch(`http://${host}:9123/pair`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ storeToken, defaultPrinter: defaultPrinter || '' }),
        signal: AbortSignal.timeout(1500)
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          writeCache({ ip: host, secret: storeToken, defaultPrinter: defaultPrinter || '' });
          return { success: true, printers: data.printers };
        }
      }
    } catch {}
  }

  return { success: false, error: 'Agent offline — start the Print Agent on the printer PC' };
}

// ── Check agent status: Server API first (works from any device), LAN fallback ──
export async function checkAgentStatus(ip?: string): Promise<{ online: boolean; printers?: string[]; version?: string; error?: string }> {
  const authHeaders = getStoreAuthHeaders();

  // Method 1: Server API (works from phone/tablet/any device)
  if (Object.keys(authHeaders).length > 0) {
    try {
      const res = await fetch('/api/printer/setup', {
        headers: authHeaders,
        signal: AbortSignal.timeout(5000)
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.agentConnected) {
          return {
            online: true,
            printers: (data.printers || []).map((p: any) => p.name || p.connectionAddress).filter(Boolean),
            version: data.agentId
          };
        }
      }
    } catch {}
  }

  // Method 2: Direct LAN (same PC only — try 127.0.0.1 then localhost)
  const checkIp = ip || cfg.ip || '127.0.0.1';
  if (checkIp !== 'cloud') {
    const targets = checkIp === '127.0.0.1' || checkIp === 'localhost' ? ['127.0.0.1', 'localhost'] : [checkIp];
    for (const host of targets) {
      try {
        const res = await fetch(`http://${host}:9123/status`, { signal: AbortSignal.timeout(1200) });
        if (res.ok) {
          const d = await res.json();
          if (d.status === 'online' || d.success) {
            return { online: true, printers: d.printers, version: d.version };
          }
        }
      } catch {}
    }
  }

  return { online: false, error: 'No print agent connected' };
}

// ── Capture receipt DOM element as Base64 PNG ──
export async function captureReceiptAsImage(elementId: string): Promise<string | null> {
  const el = document.getElementById(elementId);
  if (!el) {
    console.error('[PRINT CAPTURE ERROR] Element not found:', elementId);
    return null;
  }

  // Preserve initial inline styles
  const prevDisplay = el.style.display;
  const prevPosition = el.style.position;
  const prevLeft = el.style.left;
  const prevTop = el.style.top;
  const prevZIndex = el.style.zIndex;
  const prevVisibility = el.style.visibility;
  const prevOpacity = el.style.opacity;

  try {
    // Force mount element at top-left viewport origin with !important for html2canvas
    el.style.setProperty('display', 'block', 'important');
    el.style.setProperty('position', 'fixed', 'important');
    el.style.setProperty('left', '0px', 'important');
    el.style.setProperty('top', '0px', 'important');
    el.style.setProperty('z-index', '99999', 'important');
    el.style.setProperty('visibility', 'visible', 'important');
    el.style.setProperty('opacity', '1', 'important');

    const html2canvas = (await import('html2canvas-pro')).default;
    let canvas: HTMLCanvasElement | null = null;
    const targetWidth = Math.max(el.offsetWidth || 0, el.clientWidth || 0, 280);

    // Helper: sanitize cloned element by hiding broken or cross-origin blocked images
    const sanitizeClone = (clonedDoc: Document) => {
      try {
        const target = clonedDoc.getElementById(elementId);
        if (target) {
          target.style.setProperty('display', 'block', 'important');
          target.style.setProperty('position', 'static', 'important');
          target.style.setProperty('left', '0px', 'important');
          target.style.setProperty('width', `${targetWidth}px`, 'important');
          const imgs = target.getElementsByTagName('img');
          for (let i = 0; i < imgs.length; i++) {
            const img = imgs[i];
            if (!img.complete || img.naturalWidth === 0) {
              img.style.display = 'none';
            }
          }
        }
      } catch {}
    };

    try {
      canvas = await html2canvas(el, {
        scale: 3,
        useCORS: true,
        allowTaint: true,
        width: targetWidth,
        windowWidth: 1024,
        imageTimeout: 2000,
        backgroundColor: '#ffffff',
        logging: false,
        onclone: (clonedDoc) => {
          sanitizeClone(clonedDoc);
          const target = clonedDoc.getElementById(elementId);
          if (target) {
            (target.style as any).webkitFontSmoothing = 'none';
            target.style.textRendering = 'geometricPrecision';
            target.style.imageRendering = 'pixelated';
            target.style.backgroundColor = '#ffffff';
            target.style.color = '#000000';
          }
        },
      });
    } catch (renderErr) {
      console.warn('[PRINT CAPTURE WARN] Primary capture failed, retrying simple capture:', renderErr);
      try {
        canvas = await html2canvas(el, {
          scale: 3,
          useCORS: false,
          allowTaint: true,
          width: targetWidth,
          windowWidth: 1024,
          imageTimeout: 1000,
          backgroundColor: '#ffffff',
          logging: false,
          onclone: sanitizeClone,
        });
      } catch (fallbackErr) {
        console.error('[PRINT CAPTURE ERROR] Fallback html2canvas failed:', fallbackErr);
      }
    }

    if (!canvas || canvas.width === 0 || canvas.height === 0) {
      console.error('[PRINT CAPTURE ERROR] Canvas object is null or has 0 dimensions');
      return null;
    }

    // ════════ HIGH-CONTRAST THERMAL BINARIZATION FILTER ════════
    // Thermal printers have 1-bit heads (burn dot or don't burn dot).
    // Binarizing each pixel to pure solid black (#000000) or pure white (#ffffff)
    // guarantees razor-sharp, pitch-black, dark thermal output!
    try {
      if (canvas.width > 0 && canvas.height > 0) {
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (ctx) {
          const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const data = imgData.data;
          for (let i = 0; i < data.length; i += 4) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            const a = data[i + 3];
            if (a < 128) {
              data[i] = 255;
              data[i + 1] = 255;
              data[i + 2] = 255;
              data[i + 3] = 255;
            } else {
              // Perceived luminance (CCIR 601)
              const lum = 0.299 * r + 0.587 * g + 0.114 * b;
              // Thermal-optimized high threshold (232): ensures characters are dark, solid, and deep black without faint dropout
              const val = lum < 232 ? 0 : 255;
              data[i] = val;
              data[i + 1] = val;
              data[i + 2] = val;
              data[i + 3] = 255;
            }
          }
          ctx.putImageData(imgData, 0, 0);
        }
      }
    } catch (binErr) {
      console.warn('[PRINT CAPTURE] Binarization pass skipped:', binErr);
    }

    try {
      const dataUrl = canvas.toDataURL('image/png');
      return dataUrl.split(',')[1];
    } catch (taintErr) {
      console.warn('[PRINT CAPTURE WARN] Canvas tainted, exporting via clean buffer:', taintErr);
      const cleanCanvas = document.createElement('canvas');
      cleanCanvas.width = canvas.width;
      cleanCanvas.height = canvas.height;
      const ctx = cleanCanvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, cleanCanvas.width, cleanCanvas.height);
        ctx.drawImage(canvas, 0, 0);
        return cleanCanvas.toDataURL('image/png').split(',')[1];
      }
      return null;
    }
  } catch (e) {
    console.error('[PRINT CAPTURE ERROR]:', e);
    return null;
  } finally {
    // Always restore original element styles
    el.style.display = prevDisplay;
    el.style.position = prevPosition;
    el.style.left = prevLeft;
    el.style.top = prevTop;
    el.style.zIndex = prevZIndex;
    el.style.visibility = prevVisibility;
    el.style.opacity = prevOpacity;
  }
}

// ── Print image: Server relay first (works from any device), LAN fallback ──
export async function printReceiptImage(receiptElementId: string, storeApiKey?: string): Promise<PrintResult> {
  const cfg = readCache();
  const authHeaders = getStoreAuthHeaders(storeApiKey);
  if (!authHeaders['x-store-api-key'] && !authHeaders['Authorization']) {
    return { success: false, method: 'none', error: 'Agent not configured — open POS to auto-pair' };
  }

  // Capture receipt as image
  const imageBase64 = await captureReceiptAsImage(receiptElementId);
  if (!imageBase64) return { success: false, method: 'capture', error: 'Failed to capture receipt image' };

  // Method 1: Server dispatch (works from phone/tablet/any device)
  try {
    const res = await fetch('/api/printer/dispatch', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ receiptImageBase64: imageBase64 }),
      signal: AbortSignal.timeout(30000),
    });
    if (res.ok) {
      const data = await res.json();
      if (data.success && data.agentConnected !== false) {
        return { success: true, method: 'server-relay', printer: data.printer };
      }
      if (data.agentConnected === false || data.success === false) {
        return { success: false, method: 'server-relay', error: data.error || 'Print Agent is offline or not connected' };
      }
    } else {
      const errData = await res.json().catch(() => null);
      if (errData?.error) {
        return { success: false, method: 'server-relay', error: errData.error };
      }
      console.warn('[PrintAgent] Server dispatch returned non-OK, trying LAN fallback');
    }
  } catch (e: any) {
    console.warn('[PrintAgent] Server dispatch failed, trying LAN fallback:', e);
  }

  // Method 2: Direct LAN (same PC only — fallback)
  const agentIp = cfg.ip || '127.0.0.1';
  if (agentIp === 'cloud') {
    return { success: false, method: 'server-relay', error: 'Print Agent not connected — please ensure Kangavena Print Agent is open on your PC' };
  }

  const authToken = authHeaders['x-store-api-key'] || storeApiKey || cfg.secret || '';
  const doPrint = async (token: string) => {
    return fetch(`http://${agentIp}:9123/print`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({
        storeToken: token,
        defaultPrinter: cfg.defaultPrinter || '',
        receiptImageBase64: imageBase64,
      }),
      signal: AbortSignal.timeout(30000),
    });
  };

  try {
    let res = await doPrint(authToken);

    // Self-healing: if 401, re-pair and retry once
    if (res.status === 401 && authToken) {
      try {
        await fetch(`http://${agentIp}:9123/pair`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ storeToken: authToken, defaultPrinter: cfg.defaultPrinter || '' }),
          signal: AbortSignal.timeout(3000),
        });
        res = await doPrint(authToken);
      } catch {}
    }

    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
      return { success: false, method: 'agent-lan', error: err.error || `HTTP ${res.status}` };
    }
    const result = await res.json();
    return { success: result.success !== false, method: 'agent-lan', printer: result.printer };
  } catch (e: any) {
    return { success: false, method: 'agent-lan', error: e.name === 'AbortError' ? 'Agent timeout (30s)' : (e.message || 'Network error') };
  }
}

// ── Legacy compat (kept for non-image fallback) ──
export async function printReceipt(data: ReceiptData, storeApiKey?: string): Promise<PrintResult> {
  return printReceiptImage('kangavena-receipt-print', storeApiKey);
}
